<script setup lang="ts">
import { onMounted, computed, provide } from 'vue'

import { BlockContainer } from '@toni/brand-ui'
import {
  BauerProductCard,
  ProductCardLayout,
  PurchaseOptions
} from '@toni/commerce-api-types'

import { useIntersectionObserver, useProductCardTracking } from '../../utils/productCardTracking'
import ProductCardFull from './ProductCardFull.vue'
import ProductCardSimple from './ProductCardSimple.vue'

const props = withDefaults(defineProps<{
  bauerProductCard: BauerProductCard,
  purchaseOptions?: PurchaseOptions,
  layout?: ProductCardLayout,
  contentBlocks?: string[],
  currentContentBlockIndex?: number
}>(), {
  layout: 'FULL',
  purchaseOptions: undefined,
  contentBlocks: undefined,
  currentContentBlockIndex: undefined
})

// Create computed properties to maintain reactivity
const bauerProductCardId = computed(() => props.bauerProductCard.bauerProductCardId)
const contentBlocksComputed = computed(() => props.contentBlocks)
const productCardIndexComputed = computed(() => props.currentContentBlockIndex)

// Use the reusable tracking function for creating the tracking data
const {
  trackingData
} = useProductCardTracking(
  {
    placementType: 'product_card',
    contentBlocks: contentBlocksComputed.value,
    currentContentBlockIndex: productCardIndexComputed.value
  }
)

// Set up the intersection observer for scroll tracking
const {
  productCardScrollTrackingRef,
  setupIntersectionObserver
} = useIntersectionObserver({
  placementType: trackingData.product_card_type,
  productCardPosition: trackingData.product_card_position,
  productCardRelativePosition: trackingData.product_card_rel_position,
  bauerProductCardId: bauerProductCardId.value

})

onMounted(() => {
  if (setupIntersectionObserver) {
    setupIntersectionObserver()
  }
})

provide('productCardTracking', trackingData)
</script>

<template>
  <BlockContainer>
    <ProductCardFull v-if="layout === 'FULL'"
                     :product-card="bauerProductCard"
                     :purchase-options="purchaseOptions"
                     :embedded="true" />
    <ProductCardSimple v-else
                       :product-card="bauerProductCard"
                       :purchase-options="purchaseOptions"
                       :embedded="false" />
    <!-- Reference element for intersection observer tracking -->
    <div v-if="contentBlocks && contentBlocks.length > 0"
         ref="productCardScrollTrackingRef" />
  </BlockContainer>
</template>
